import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsContent } from '@/components/ui/tabs';

import {
    useMarkAllNotificationsRead,
    useMarkNotificationRead,
    useNotificationsList,
    useUnreadCounts,
} from '../hooks/useNotifications';
import { useNotificationsSocket } from '../hooks/useNotificationsSocket';
import { NOTIFICATION_TABS, type INotification, type NotificationType } from '../types';
import { NotificationEmptyState } from './NotificationEmptyState';
import { NotificationIcon } from './NotificationIcon';
import { NotificationRow } from './NotificationRow';
import { NotificationTabsList } from './NotificationTabsList';
import { NotificationsViewAllDrawer } from './NotificationsViewAllDrawer';

interface NotificationListProps {
    type: NotificationType;
    enabled: boolean;
    onMarkRead: (id: string) => void;
    onNavigate: (notification: INotification) => void;
}

function NotificationList({ type, enabled, onMarkRead, onNavigate }: NotificationListProps) {
    const { data, isLoading } = useNotificationsList(type, enabled);

    if (isLoading) {
        return (
            <div className="flex h-[360px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    const items = data?.items ?? [];
    if (items.length === 0) {
        return <NotificationEmptyState type={type} className="h-[360px]" />;
    }

    return (
        <ScrollArea className="h-[360px]">
            <div className="divide-y divide-border-default">
                {items.map((n) => (
                    <NotificationRow
                        key={n.id}
                        notification={n}
                        onMarkRead={onMarkRead}
                        onNavigate={onNavigate}
                    />
                ))}
            </div>
        </ScrollArea>
    );
}

/**
 * Header notification bell: a single button with an unread badge that opens a
 * popover with Messages / Alerts tabs. Live-updates over the `/notifications`
 * socket. Hovering a row reveals "mark as read".
 */
export function NotificationBell() {
    useNotificationsSocket();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [tab, setTab] = useState<NotificationType>('MESSAGE');

    const { data: counts } = useUnreadCounts();
    const markRead = useMarkNotificationRead();
    const markAll = useMarkAllNotificationsRead();

    const messageUnread = counts?.messageUnread ?? 0;
    const alertUnread = counts?.alertUnread ?? 0;
    const total = messageUnread + alertUnread;

    const handleNavigate = (n: INotification) => {
        if (!n.isRead) markRead.mutate(n.id);
        if (n.actionUrl) {
            setOpen(false);
            navigate(n.actionUrl);
        }
    };

    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        size="icon-sm"
                        className="relative"
                        aria-label={t('notifications.title', 'Notifications')}
                    >
                        <NotificationIcon name="Bell" className="size-4.5" />
                        {total > 0 && (
                            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
                                {total > 99 ? '99+' : total}
                            </span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={8} className="w-[380px] p-0 gap-0">
                    <div className="flex items-center justify-between border-b px-4 py-3">
                        <h3 className="text-sm font-semibold">
                            {t('notifications.title', 'Notifications')}
                        </h3>
                        <Button
                            type="button"
                            variant="ghost"
                            size="xs"
                            className="gap-1 text-xs text-muted-foreground"
                            onClick={() => markAll.mutate(tab)}
                            disabled={markAll.isPending}
                        >
                            <NotificationIcon name="CheckCheck" className="size-3.5" />
                            {t('notifications.markAllRead', 'Mark all as read')}
                        </Button>
                    </div>
                    <Tabs
                        value={tab}
                        onValueChange={(v) => setTab(v as NotificationType)}
                        className="gap-2 p-2"
                    >
                        <div>
                            <NotificationTabsList
                                messageUnread={messageUnread}
                                alertUnread={alertUnread}
                            />
                        </div>
                        {NOTIFICATION_TABS.map((value) => (
                            <TabsContent key={value} value={value}>
                                <NotificationList
                                    type={value}
                                    enabled={open && tab === value}
                                    onMarkRead={(id) => markRead.mutate(id)}
                                    onNavigate={handleNavigate}
                                />
                            </TabsContent>
                        ))}
                    </Tabs>
                    <div className="border-t p-1.5">
                        <Button
                            type="button"
                            variant="ghost"
                            className="w-full justify-center text-sm font-medium text-primary hover:text-primary"
                            onClick={() => {
                                setOpen(false);
                                setDrawerOpen(true);
                            }}
                        >
                            {t('notifications.viewAll', 'View all')}
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>
            <NotificationsViewAllDrawer
                open={drawerOpen}
                onOpenChange={setDrawerOpen}
                tab={tab}
                onTabChange={setTab}
            />
        </>
    );
}
